import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Package, MapPin, RefreshCcw,
  Plus, Check, ArrowRightLeft,
  ClipboardList, Search, Layers, X,
  AlertCircle, Filter, Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import {
  fetchAllPaginated,
  getProductStockForSite,
  hydrateLegacyVariantStock,
  resolvePreferredStockSiteId,
} from '../utils/productCatalog';
import { normalizeBrandLabel } from '../utils/brandFilters';
import { applyActiveProductsFilter } from '../utils/productStatus';
import { isVariantTracked, listStockVariantOptions } from '../utils/variantStock';

const formatCantidad = (value) => Number(value || 0).toLocaleString('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Transferencia de Mercadería - Estándar Impulza (Modelo Productos)
 * - Header + KPIs
 * - Layout compacto (menos gaps)
 * - Selección de productos igual que Ajuste masivo: filtros (texto, categoría,
 *   marca, tipo, solo con stock en origen) + lista clickeable + "Agregar los
 *   filtrados" + tabla editable con cantidad por línea.
 * - Confirmación verde con animación de check (estándar)
 * - Sin botón atrás / sin "auditoría habilitada" / sin "confirmar lote"
 */
const InventarioTransferencia = ({ onNavigate }) => {
  const { profile, organization } = useAuth();

  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [defaultSiteId, setDefaultSiteId] = useState('');

  const [sitios, setSitios] = useState([]);
  const [productos, setProductos] = useState([]);

  const [origen, setOrigen] = useState({ sitioId: '', deposito: '' });
  const [destino, setDestino] = useState({ sitioId: '', deposito: '' });
  const [depositosOrigen, setDepositosOrigen] = useState([]);
  const [depositosDestino, setDepositosDestino] = useState([]);

  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros del panel de selección (mismo criterio que Ajuste masivo)
  const [query, setQuery] = useState('');
  const [marcaFiltro, setMarcaFiltro] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  // En una transferencia sin stock en origen no hay nada que mover: arranca tildado.
  const [soloConStock, setSoloConStock] = useState(true);

  // Líneas del lote: [{ key, productId, variantId, cantidad }]. El producto se
  // resuelve contra el catálogo vigente para que el stock se refresque al
  // cambiar el origen o al recargar.
  const [lineas, setLineas] = useState([]);
  const [cantidadGlobal, setCantidadGlobal] = useState('');
  const lineaSeq = useRef(0);
  const nextKey = () => {
    lineaSeq.current += 1;
    return `l${lineaSeq.current}`;
  };

  useEffect(() => {
    if (profile?.organization_id) fetchMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    const siteId = destino.sitioId || origen.sitioId;
    if (siteId) persistSelectedSiteId(siteId);
  }, [origen.sitioId, destino.sitioId]);

  const fetchMasters = async () => {
    try {
      const orgId = profile.organization_id;
      // resOrg sale del AuthContext (no se refetchea)
      const resOrg = { data: organization };
      setLoading(true);
      const [resSitios, resCategorias] = await Promise.all([
        supabase.from('sitios').select('*').eq('organization_id', orgId).order('nombre'),
        supabase.from('product_categories').select('id, name').eq('organization_id', orgId).order('name'),
      ]);
      if (resCategorias.error) {
        console.warn('[InventarioTransferencia] categorias no disponibles', resCategorias.error);
      }
      setCategorias(resCategorias.data || []);

      const availableSites = resSitios.data || [];
      const resolvedDefaultSiteId = resolvePreferredStockSiteId(resOrg.data, availableSites);

      const legacyStockResult = await hydrateLegacyVariantStock({
        supabase,
        organizationId: orgId,
        defaultSiteId: resolvedDefaultSiteId,
      });

      // Talles y colores: variantes y stock por talle. Si la consulta extendida
      // falla (esquema no aplicado en esta base) se usa la de siempre.
      const fetchProductos = (variantSelect) => fetchAllPaginated(() =>
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select(`*, product_categories(name), stock_levels(*), ${variantSelect}`)
          .eq('organization_id', orgId)
        ).order('name', { ascending: true })
      );
      let productosData;
      try {
        productosData = await fetchProductos('product_variants(id, stock_quantity, talle, talle_orden, color_id, activo, archivado_at, attributes, colores(id, nombre, hex)), stock_variantes(variant_id, site_id, quantity)');
      } catch (variantSelectError) {
        console.error('Transferencia: catálogo con talles no disponible, uso el clásico:', variantSelectError);
        productosData = await fetchProductos('product_variants(stock_quantity)');
      }

      setSitios(availableSites);
      setProductos(productosData || []);
      setDefaultSiteId(resolvedDefaultSiteId || '');
      setOrigen((prev) => ({
        ...prev,
        sitioId: resolvePreferredSiteId(availableSites, {
          currentSiteId: prev.sitioId,
        }),
      }));

      if (legacyStockResult.inserted > 0) {
        toast.success(`Se recuperó el stock importado en ${legacyStockResult.inserted} productos.`);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const s = sitios.find((sit) => sit.id === origen.sitioId);
    setDepositosOrigen(s?.depositos || []);
    setOrigen((prev) => ({ ...prev, deposito: '' }));
  }, [origen.sitioId, sitios]);

  useEffect(() => {
    const s = sitios.find((sit) => sit.id === destino.sitioId);
    setDepositosDestino(s?.depositos || []);
    setDestino((prev) => ({ ...prev, deposito: '' }));
  }, [destino.sitioId, sitios]);

  const sitioStockOrigen = origen.sitioId || defaultSiteId || null;

  const productoMap = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);

  const categoriaMap = useMemo(
    () => new Map(categorias.map((categoria) => [categoria.id, categoria.name])),
    [categorias]
  );

  const nombreCategoria = (product) =>
    product?.product_categories?.name
    || categoriaMap.get(product?.category_id)
    || product?.category
    || 'Sin categoria';

  const marcas = useMemo(() => {
    const set = new Set();
    productos.forEach((product) => {
      const brand = normalizeBrandLabel(product.brand, '');
      if (brand) set.add(brand);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [productos]);

  const tiposProducto = useMemo(() => {
    const set = new Set();
    productos.forEach((product) => {
      const tipo = String(product.product_type || '').trim();
      if (tipo) set.add(tipo);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [productos]);

  // Stock en origen: por talle es la suma de los talles activos.
  const stockOrigenProducto = (product) => (
    isVariantTracked(product)
      ? listStockVariantOptions(product, sitioStockOrigen).reduce((acc, option) => acc + Number(option.stock || 0), 0)
      : getProductStockForSite(product, sitioStockOrigen)
  );

  const lineasPorProducto = useMemo(() => {
    const map = new Map();
    lineas.forEach((linea) => map.set(linea.productId, (map.get(linea.productId) || 0) + 1));
    return map;
  }, [lineas]);

  const productosFiltrados = useMemo(() => {
    const term = query.trim().toLowerCase();
    return productos
      .filter((product) => {
        if (marcaFiltro && normalizeBrandLabel(product.brand, '') !== marcaFiltro) return false;
        if (categoriaFiltro === '__sin__') {
          if (product.category_id) return false;
        } else if (categoriaFiltro && product.category_id !== categoriaFiltro) return false;
        if (tipoFiltro && String(product.product_type || '').trim() !== tipoFiltro) return false;
        if (soloConStock && stockOrigenProducto(product) <= 0) return false;
        if (!term) return true;
        const name = (product.name || '').toLowerCase();
        const barcode = (product.barcode || '').toLowerCase();
        const brand = normalizeBrandLabel(product.brand, '').toLowerCase();
        return name.includes(term) || barcode.includes(term) || brand.includes(term);
      })
      .slice(0, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaFiltro, marcaFiltro, productos, query, sitioStockOrigen, soloConStock, tipoFiltro]);

  // Por talle: si hay un solo talle con stock en origen se preselecciona.
  const buildLinea = (producto) => {
    let variantId = '';
    if (isVariantTracked(producto)) {
      const conStock = listStockVariantOptions(producto, sitioStockOrigen).filter((option) => option.stock > 0);
      if (conStock.length === 1) variantId = conStock[0].id;
    }
    return { key: nextKey(), productId: producto.id, variantId, cantidad: cantidadGlobal || '' };
  };

  const agregarProducto = (producto) => {
    const porTalleProd = isVariantTracked(producto);
    if (porTalleProd && listStockVariantOptions(producto, sitioStockOrigen).length === 0) {
      toast.error(`"${producto.name}" no tiene talles activos`);
      return;
    }
    if (lineasPorProducto.has(producto.id)) {
      if (!porTalleProd) {
        toast('Ese producto ya está en la tabla', { icon: 'ℹ️' });
        return;
      }
      if (lineas.some((linea) => linea.productId === producto.id && !linea.variantId)) {
        toast('Elegí el talle en la línea que ya está cargada', { icon: 'ℹ️' });
        return;
      }
    }
    setLineas((prev) => [...prev, buildLinea(producto)]);
  };

  const agregarTodosLosFiltrados = () => {
    const nuevos = productosFiltrados.filter((product) => (
      !lineasPorProducto.has(product.id)
      && (!isVariantTracked(product) || listStockVariantOptions(product, sitioStockOrigen).length > 0)
    ));
    if (nuevos.length === 0) {
      toast('No hay productos nuevos para agregar', { icon: 'ℹ️' });
      return;
    }
    setLineas((prev) => [...prev, ...nuevos.map(buildLinea)]);
    toast.success(`${nuevos.length} producto(s) agregado(s)`);
  };

  const agregarTalle = (productId) => {
    if (lineas.some((linea) => linea.productId === productId && !linea.variantId)) {
      toast('Ya hay una línea de ese producto esperando talle', { icon: 'ℹ️' });
      return;
    }
    setLineas((prev) => {
      const ultima = prev.map((l) => l.productId).lastIndexOf(productId);
      const nueva = { key: nextKey(), productId, variantId: '', cantidad: cantidadGlobal || '' };
      const copia = [...prev];
      copia.splice(ultima + 1, 0, nueva);
      return copia;
    });
  };

  const quitarLinea = (key) => setLineas((prev) => prev.filter((linea) => linea.key !== key));

  const actualizarLinea = (key, patch) => {
    setLineas((prev) => prev.map((linea) => (linea.key === key ? { ...linea, ...patch } : linea)));
  };

  const aplicarCantidadATodos = () => {
    const value = Number(cantidadGlobal);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Ingresá una cantidad válida para aplicar');
      return;
    }
    setLineas((prev) => prev.map((linea) => ({ ...linea, cantidad: String(value) })));
  };

  // Cada línea con su stock en origen y su validación.
  const lineasCalculadas = useMemo(() => {
    const claves = new Map();
    lineas.forEach((linea) => {
      if (!linea.variantId && isVariantTracked(productoMap.get(linea.productId))) return;
      const clave = `${linea.productId}:${linea.variantId || ''}`;
      claves.set(clave, (claves.get(clave) || 0) + 1);
    });

    return lineas.map((linea) => {
      const producto = productoMap.get(linea.productId) || null;
      if (!producto) {
        return { ...linea, producto: null, porTalle: false, opciones: [], opcion: null, stockOrigen: 0, cantidadNumerica: 0, stockResultante: 0, error: 'Producto no disponible' };
      }
      const porTalleLinea = isVariantTracked(producto);
      const opciones = porTalleLinea ? listStockVariantOptions(producto, sitioStockOrigen) : [];
      const opcion = porTalleLinea ? opciones.find((option) => option.id === linea.variantId) || null : null;
      const stockOrigen = porTalleLinea
        ? Number(opcion?.stock || 0)
        : getProductStockForSite(producto, sitioStockOrigen);
      const cantidadNumerica = Number(linea.cantidad || 0);
      const cantidadValida = Number.isFinite(cantidadNumerica) && cantidadNumerica > 0;
      const repetida = (claves.get(`${linea.productId}:${linea.variantId || ''}`) || 0) > 1;

      let error = '';
      if (porTalleLinea && !opcion) error = 'Elegí el talle/color';
      else if (repetida) error = porTalleLinea ? 'Talle repetido' : 'Producto repetido';
      else if (!cantidadValida) error = 'Cantidad inválida';
      else if (cantidadNumerica > stockOrigen) error = 'Stock insuficiente en origen';

      return {
        ...linea,
        producto,
        porTalle: porTalleLinea,
        opciones,
        opcion,
        stockOrigen,
        cantidadNumerica: cantidadValida ? cantidadNumerica : 0,
        stockResultante: stockOrigen - (cantidadValida ? cantidadNumerica : 0),
        error,
      };
    });
  }, [lineas, productoMap, sitioStockOrigen]);

  const conError = useMemo(() => lineasCalculadas.filter((linea) => linea.error).length, [lineasCalculadas]);

  const totalUnidades = useMemo(
    () => lineasCalculadas.reduce((acc, linea) => acc + Number(linea.cantidadNumerica || 0), 0),
    [lineasCalculadas]
  );
  // Si todas las líneas comparten la misma unidad la mostramos; si hay mezcla,
  // el total se rotula genéricamente porque sumar kg con un. no tiene sentido.
  const unidadLoteCompartida = useMemo(() => {
    if (lineasCalculadas.length === 0) return '';
    const first = (lineasCalculadas[0]?.producto?.unit_of_measure || '').trim();
    if (!first) return '';
    return lineasCalculadas.every((linea) => (linea.producto?.unit_of_measure || '').trim() === first) ? first : '';
  }, [lineasCalculadas]);

  const resetForm = () => {
    setLineas([]);
    setCantidadGlobal('');
    setOrigen({
      sitioId: resolvePreferredSiteId(sitios, {
        currentSiteId: origen.sitioId,
      }),
      deposito: '',
    });
    setDestino({ sitioId: '', deposito: '' });
    setIsSuccess(false);
  };

  const confirmarTransferencia = async () => {
    if (lineasCalculadas.length === 0) return toast.error('No hay productos en la lista');
    if (conError > 0) return toast.error('Corregí las líneas marcadas antes de confirmar');
    if (!origen.sitioId || !destino.sitioId) return toast.error('Seleccioná origen y destino');
    // ✅ FIX multi-sucursal: permitimos transferir entre depósitos del mismo sitio si los depósitos difieren.
    // Solo bloqueamos cuando origen y destino son idénticos en sitio Y depósito.
    const mismoSitio = origen.sitioId === destino.sitioId;
    const mismoDeposito = (origen.deposito || '') === (destino.deposito || '');
    if (mismoSitio && mismoDeposito) {
      return toast.error('Origen y destino no pueden ser idénticos. Cambiá la sucursal o el depósito.');
    }

    const itemsList = lineasCalculadas.map((linea) => ({
      ...linea.producto,
      lineaKey: linea.key,
      cantidad: linea.cantidadNumerica,
      variantId: linea.opcion?.id || null,
      variantLabel: linea.opcion?.label || '',
    }));
    // Se procesa línea por línea: si una falla, las anteriores ya se movieron.
    // Las sacamos de la tabla para que un reintento no las transfiera dos veces.
    const procesadas = [];

    setSaving(true);
    try {
      const orgId = profile.organization_id;

      const origenSitioNombreLote = sitios.find((s) => s.id === origen.sitioId)?.nombre;
      const destinoSitioNombreLote = sitios.find((s) => s.id === destino.sitioId)?.nombre;

      for (const item of itemsList) {
        // Talles y colores: la variante se transfiere en la base, atómico
        // (control de stock, ambas sucursales y el movimiento con variant_id).
        if (item.variantId) {
          const { error: variantTransferError } = await supabase.rpc('fn_transferir_stock_variante', {
            p_org: orgId,
            p_product: item.id,
            p_variant: item.variantId,
            p_origen: origen.sitioId,
            p_destino: destino.sitioId,
            p_cantidad: Number(item.cantidad),
            p_deposito_origen: origen.deposito || null,
            p_deposito_destino: destino.deposito || null,
            p_motivo: `Transf. desde ${origen.deposito || 'Principal'} (${origenSitioNombreLote}) hacia ${destino.deposito || 'Principal'} (${destinoSitioNombreLote})`,
          });
          if (variantTransferError) {
            throw new Error(`${item.name} ${item.variantLabel || ''}: ${variantTransferError.message || 'no se pudo transferir'}`.trim());
          }
          procesadas.push(item.lineaKey);
          continue;
        }

        const { data: originLevel, error: originError } = await supabase
          .from('stock_levels')
          .select('quantity')
          .eq('product_id', item.id)
          .eq('site_id', origen.sitioId)
          .maybeSingle();

        if (originError) throw originError;

        const stOrigen = Number(originLevel?.quantity || 0);
        if (Number(item.cantidad) > stOrigen) {
          throw new Error(`${item.name}: stock insuficiente en origen. Disponible ${formatCantidad(stOrigen)}.`);
        }

        const { error: originUpsertError } = await supabase.from('stock_levels').upsert(
          {
            product_id: item.id,
            site_id: origen.sitioId,
            quantity: stOrigen - Number(item.cantidad),
            organization_id: orgId,
          },
          { onConflict: 'product_id,site_id' }
        );
        if (originUpsertError) throw originUpsertError;

        const { data: currentDest, error: currentDestError } = await supabase
          .from('stock_levels')
          .select('quantity')
          .eq('product_id', item.id)
          .eq('site_id', destino.sitioId)
          .maybeSingle();
        if (currentDestError) throw currentDestError;

        const { error: destUpsertError } = await supabase.from('stock_levels').upsert(
          {
            product_id: item.id,
            site_id: destino.sitioId,
            quantity: Number(currentDest?.quantity || 0) + Number(item.cantidad),
            organization_id: orgId,
          },
          { onConflict: 'product_id,site_id' }
        );
        if (destUpsertError) throw destUpsertError;

        const origenSitioNombre = sitios.find((s) => s.id === origen.sitioId)?.nombre;
        const destinoSitioNombre = sitios.find((s) => s.id === destino.sitioId)?.nombre;

        const { error: movementError } = await supabase.from('movimientos_stock').insert([
          {
            producto_id: item.id,
            tipo: 'Transferencia',
            cantidad: item.cantidad,
            motivo: `Transf. desde ${origen.deposito || 'Principal'} (${origenSitioNombre}) hacia ${destino.deposito || 'Principal'} (${destinoSitioNombre})`,
            sitio_origen: origen.sitioId,
            sitio_destino: destino.sitioId,
            // ✅ FIX multi-depósito: persistimos el depósito de origen y destino para trazabilidad real
            deposito_origen: origen.deposito || null,
            deposito_destino: destino.deposito || null,
            organization_id: orgId,
          },
        ]);
        if (movementError) throw movementError;
        procesadas.push(item.lineaKey);
      }

      setIsSuccess(true);
      toast.success('Transferencia realizada');

      setTimeout(() => {
        resetForm();
        fetchMasters();
      }, 1600);
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error al procesar la transferencia');
      if (procesadas.length > 0) {
        setLineas((prev) => prev.filter((linea) => !procesadas.includes(linea.key)));
        toast(`${procesadas.length} línea(s) se transfirieron antes del error y se quitaron de la tabla.`, { icon: 'ℹ️', duration: 6000 });
        fetchMasters();
      }
    } finally {
      setSaving(false);
    }
  };

  const origenNombre = useMemo(
    () => sitios.find((s) => s.id === origen.sitioId)?.nombre || '-',
    [sitios, origen.sitioId]
  );
  const destinoNombre = useMemo(
    () => sitios.find((s) => s.id === destino.sitioId)?.nombre || '-',
    [sitios, destino.sitioId]
  );

  const rutaLabel = useMemo(() => {
    const oDep = origen.deposito ? ` · ${origen.deposito}` : '';
    const dDep = destino.deposito ? ` · ${destino.deposito}` : '';
    return `${origenNombre}${oDep} → ${destinoNombre}${dDep}`;
  }, [origenNombre, destinoNombre, origen.deposito, destino.deposito]);

  const estadoOperacion = useMemo(() => {
    if (isSuccess) return { label: 'Completada', tone: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (saving) return { label: 'Procesando', tone: 'text-blue-700', bg: 'bg-blue-50' };
    if (!origen.sitioId || !destino.sitioId) return { label: 'Definí la ruta', tone: 'text-slate-600', bg: 'bg-slate-50' };
    if (lineas.length === 0) return { label: 'Sin items', tone: 'text-slate-600', bg: 'bg-slate-50' };
    if (conError > 0) return { label: 'Revisar líneas', tone: 'text-rose-700', bg: 'bg-rose-50' };
    return { label: 'Listo para transferir', tone: 'text-amber-700', bg: 'bg-amber-50' };
  }, [isSuccess, saving, lineas.length, conError, origen.sitioId, destino.sitioId]);

  // ✅ FIX bug #7 multi-sucursal/depósito: alineamos canConfirm con la validación
  // que ya estaba en confirmarTransferencia (mismo sitio + distinto depósito está
  // permitido). Antes el botón quedaba bloqueado en ese caso aunque la lógica
  // posterior lo aceptaba: el usuario nunca podía completar el flujo.
  const mismoSitioCheck = origen.sitioId === destino.sitioId;
  const mismoDepositoCheck = (origen.deposito || '') === (destino.deposito || '');
  const rutaValida = !!origen.sitioId
    && !!destino.sitioId
    && !(mismoSitioCheck && mismoDepositoCheck);

  const canConfirm = lineasCalculadas.length > 0
    && conError === 0
    && !loading
    && rutaValida
    && !saving
    && !isSuccess;

  return (
    <div className="imp-page imp-container animate-nova-in">
      {/* HEADER (Modelo Productos) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ArrowRightLeft size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 uppercase tracking-tight truncate">
              Transferencia de mercadería
            </h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Movimiento entre sucursales y depósitos
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={resetForm}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <X size={14} /> Cancelar
          </button>

          <button
            type="button"
            onClick={confirmarTransferencia}
            disabled={!canConfirm}
            className={`px-5 py-2.5 rounded-2xl shadow-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
              isSuccess ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:opacity-95'
            }`}
          >
            {isSuccess ? (
              <span className="inline-flex items-center gap-2 animate-check-in">
                <Check size={16} strokeWidth={3} /> Confirmado
              </span>
            ) : saving ? (
              <>
                <RefreshCcw size={16} className="animate-spin" /> Procesando
              </>
            ) : (
              <>
                <Check size={16} strokeWidth={3} className="text-[#3FF2AA]" /> Confirmar
              </>
            )}
          </button>
        </div>
      </div>

      {/* KPIs compactos */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-blue-50 text-[#51A7FD] shadow-inner">
            <Package size={22} />
          </div>
          <div>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Items en lote</p>
            <h3 className="text-xl font-black text-slate-800 tracking-tighter">{lineas.length}</h3>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-indigo-50 text-indigo-700 shadow-inner">
            <Layers size={22} />
          </div>
          <div>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
              {unidadLoteCompartida ? `Total (${unidadLoteCompartida})` : 'Cantidad total'}
            </p>
            <h3 className="text-xl font-black text-slate-800 tracking-tighter">
              {formatCantidad(totalUnidades)}
              {unidadLoteCompartida && (
                <span className="text-[10px] text-slate-400 font-bold ml-1">{unidadLoteCompartida}</span>
              )}
            </h3>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 min-w-0">
          <div className="p-3 rounded-xl bg-slate-50 text-slate-500 shadow-inner">
            <MapPin size={22} />
          </div>
          <div className="min-w-0">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Ruta</p>
            <div className="text-[11px] font-black text-slate-800 uppercase tracking-tight truncate">
              {rutaLabel}
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className={`p-3 rounded-xl ${conError > 0 ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-500'} shadow-inner`}>
            <AlertCircle size={22} />
          </div>
          <div>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Con error</p>
            <h3 className={`text-xl font-black tracking-tighter ${conError > 0 ? 'text-rose-700' : 'text-slate-800'}`}>{conError}</h3>
          </div>
        </div>
      </div>

      {/* BLOQUES */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Izquierda */}
        <div className="lg:col-span-2 space-y-3">
          {/* Origen/Destino */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <h2 className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <MapPin size={14} className="text-rose-500" /> Origen
                </h2>

                <select
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase outline-none focus:ring-4 focus:ring-rose-500/5 focus:border-rose-400 transition-all shadow-inner"
                  value={origen.sitioId}
                  onChange={(e) => setOrigen({ ...origen, sitioId: e.target.value })}
                >
                  <option value="">Seleccionar sucursal...</option>
                  {sitios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>

                <select
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase outline-none disabled:opacity-60 transition-all shadow-inner"
                  value={origen.deposito}
                  onChange={(e) => setOrigen({ ...origen, deposito: e.target.value })}
                  disabled={!origen.sitioId}
                >
                  <option value="">Seleccionar depósito...</option>
                  {depositosOrigen.map((d, i) => (
                    <option key={i} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 md:border-l md:pl-4 border-slate-100">
                <h2 className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <MapPin size={14} className="text-emerald-500" /> Destino
                </h2>

                <select
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-400 transition-all shadow-inner"
                  value={destino.sitioId}
                  onChange={(e) => setDestino({ ...destino, sitioId: e.target.value })}
                >
                  <option value="">Seleccionar sucursal...</option>
                  {sitios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>

                <select
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase outline-none disabled:opacity-60 transition-all shadow-inner"
                  value={destino.deposito}
                  onChange={(e) => setDestino({ ...destino, deposito: e.target.value })}
                  disabled={!destino.sitioId}
                >
                  <option value="">Seleccionar depósito...</option>
                  {depositosDestino.map((d, i) => (
                    <option key={i} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* PANEL DE SELECCION (mismo patrón que Ajuste masivo) */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <h2 className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Filter size={14} className="text-[#51A7FD]" /> Filtrar y seleccionar productos
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="BUSCAR NOMBRE, MARCA O CODIGO..."
                  className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase"
                />
              </div>

              <div className="md:col-span-3">
                <select
                  value={categoriaFiltro}
                  onChange={(event) => setCategoriaFiltro(event.target.value)}
                  disabled={categorias.length === 0}
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer disabled:opacity-50"
                >
                  <option value="">Todas las categorias</option>
                  <option value="__sin__">Sin categoria</option>
                  {categorias.map((categoria) => (
                    <option key={categoria.id} value={categoria.id}>{categoria.name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <select
                  value={marcaFiltro}
                  onChange={(event) => setMarcaFiltro(event.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
                >
                  <option value="">Todas las marcas</option>
                  {marcas.map((marca) => (
                    <option key={marca} value={marca}>{marca}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <select
                  value={tipoFiltro}
                  onChange={(event) => setTipoFiltro(event.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
                >
                  <option value="">Todo tipo</option>
                  {tiposProducto.map((tipo) => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={soloConStock}
                    onChange={(event) => setSoloConStock(event.target.checked)}
                    className="accent-[#51A7FD]"
                  />
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Solo con stock en origen</span>
                </label>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                  {loading ? 'Cargando productos...' : `${productosFiltrados.length} resultado(s) · maximo 120 en pantalla`}
                </p>
              </div>
              <button
                type="button"
                onClick={agregarTodosLosFiltrados}
                disabled={productosFiltrados.length === 0 || saving || isSuccess}
                className="px-4 py-2 rounded-xl bg-blue-50 text-[#51A7FD] border border-blue-100 text-[9px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-blue-100 transition-all disabled:opacity-50"
              >
                <Plus size={12} /> Agregar los filtrados
              </button>
            </div>

            <div className="mt-3 max-h-64 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
              {productosFiltrados.map((product) => {
                const porTalleProd = isVariantTracked(product);
                const enTabla = lineasPorProducto.get(product.id) || 0;
                const bloqueado = enTabla > 0 && !porTalleProd;
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => agregarProducto(product)}
                    disabled={bloqueado || saving || isSuccess}
                    className={`w-full text-left px-4 py-2.5 transition-all flex items-center justify-between gap-3 ${
                      enTabla > 0 ? 'bg-emerald-50/60' : 'hover:bg-blue-50'
                    } ${bloqueado ? 'cursor-default' : ''}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 truncate">
                        {product.name}
                        {porTalleProd && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100 text-[8px] font-black tracking-widest">
                            Talles
                          </span>
                        )}
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate">
                        {normalizeBrandLabel(product.brand)} · {nombreCategoria(product)} · {product.barcode || 'S/COD'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[10px] font-black text-[#51A7FD]">
                        {formatCantidad(stockOrigenProducto(product))} {product.unit_of_measure || 'un'}
                      </span>
                      {enTabla > 0 ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <Check size={14} />
                          {porTalleProd && enTabla > 1 && <span className="text-[9px] font-black">{enTabla}</span>}
                        </span>
                      ) : (
                        <Plus size={14} className="text-slate-300" />
                      )}
                    </div>
                  </button>
                );
              })}

              {!loading && productosFiltrados.length === 0 && (
                <div className="px-4 py-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                  {soloConStock && !origen.sitioId ? 'Elegí el origen para ver su stock' : 'Sin resultados'}
                </div>
              )}
            </div>
          </div>

          {/* TABLA DEL LOTE */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <h2 className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Package size={14} className="text-[#51A7FD]" /> Productos a transferir
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cantidadGlobal}
                    onChange={(event) => setCantidadGlobal(event.target.value)}
                    placeholder="CANT."
                    className="w-20 bg-slate-50 border border-slate-100 px-2 py-2 rounded-xl text-[10px] font-black text-right outline-none focus:border-[#51A7FD]"
                  />
                  <button
                    type="button"
                    onClick={aplicarCantidadATodos}
                    disabled={lineas.length === 0}
                    className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 border border-slate-200 text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
                  >
                    Aplicar a todos
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setLineas([])}
                  disabled={lineas.length === 0 || saving}
                  className="px-3 py-2 rounded-xl bg-white text-slate-600 border border-slate-200 text-[9px] font-black uppercase tracking-widest flex items-center gap-1 disabled:opacity-50"
                >
                  <X size={12} /> Vaciar
                </button>
              </div>
            </div>

            {lineasCalculadas.length === 0 ? (
              <div className="py-14 text-center text-slate-300 font-bold uppercase text-[9px] italic tracking-widest opacity-60">
                Elegí productos del panel de arriba para armar el lote
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead className="bg-slate-50">
                    <tr className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                      <th className="text-left px-4 py-3">Producto</th>
                      <th className="text-left px-3 py-3">Talle / color</th>
                      <th className="text-right px-3 py-3">Stock origen</th>
                      <th className="text-right px-3 py-3">Cantidad</th>
                      <th className="text-right px-3 py-3">Queda en origen</th>
                      <th className="px-3 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {lineasCalculadas.map((linea) => (
                      <tr key={linea.key} className={linea.error ? 'bg-rose-50/40' : ''}>
                        <td className="px-4 py-2.5 min-w-[200px]">
                          <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 truncate">
                            {linea.producto?.name || 'Producto no disponible'}
                          </div>
                          {linea.producto && (
                            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate">
                              {normalizeBrandLabel(linea.producto.brand)} · {nombreCategoria(linea.producto)} · {linea.producto.barcode || 'S/COD'}
                            </div>
                          )}
                          {linea.error && (
                            <div className="text-[9px] font-black uppercase tracking-widest text-rose-600 mt-1">
                              {linea.error}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {linea.porTalle ? (
                            <div className="flex items-center gap-1">
                              <select
                                value={linea.variantId || ''}
                                onChange={(event) => actualizarLinea(linea.key, { variantId: event.target.value })}
                                className={`w-36 bg-slate-50 border px-2 py-2 rounded-xl text-[10px] font-black uppercase outline-none focus:border-[#51A7FD] ${
                                  !linea.opcion ? 'border-rose-200' : 'border-slate-100'
                                }`}
                              >
                                <option value="">Elegir...</option>
                                {linea.opciones.map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {option.label} ({formatCantidad(option.stock)})
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => agregarTalle(linea.productId)}
                                className="p-2 rounded-xl text-slate-400 hover:text-[#51A7FD] hover:bg-blue-50 transition-all"
                                title="Agregar otro talle de este producto"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] font-bold text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-600 whitespace-nowrap">
                          {formatCantidad(linea.stockOrigen)} {linea.producto?.unit_of_measure || 'un'}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={linea.cantidad}
                            onFocus={(event) => event.target.select()}
                            onChange={(event) => actualizarLinea(linea.key, { cantidad: event.target.value })}
                            placeholder="0.00"
                            className={`w-24 bg-slate-50 border px-2 py-2 rounded-xl text-[11px] font-black text-right outline-none focus:border-[#51A7FD] ${
                              linea.error ? 'border-rose-200' : 'border-slate-100'
                            }`}
                          />
                        </td>
                        <td className={`px-3 py-2.5 text-right text-[11px] font-black whitespace-nowrap ${
                          linea.stockResultante < 0 ? 'text-rose-600' : 'text-slate-700'
                        }`}>
                          {formatCantidad(linea.stockResultante)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => quitarLinea(linea.key)}
                            className="p-2 rounded-xl text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-all"
                            title="Quitar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        
        {/* Derecha (Resumen) */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            <h2 className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <ClipboardList size={14} className="text-slate-400" /> Resumen
            </h2>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 shadow-inner">
              <div className="flex justify-between items-center">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Items</span>
                <span className="text-[12px] font-black text-slate-800">{lineas.length}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                  {unidadLoteCompartida ? `Total (${unidadLoteCompartida})` : 'Cantidad total'}
                </span>
                <span className="text-[12px] font-black text-slate-800">
                  {formatCantidad(totalUnidades)}
                  {unidadLoteCompartida && (
                    <span className="text-[9px] text-slate-400 font-bold ml-1">{unidadLoteCompartida}</span>
                  )}
                </span>
              </div>

              <div className="h-px bg-slate-200 opacity-60" />

              <div className="flex justify-between items-center gap-3">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Origen</span>
                <span className="text-[10px] font-black text-slate-700 uppercase text-right">
                  {origenNombre}{origen.deposito ? ` · ${origen.deposito}` : ''}
                </span>
              </div>

              <div className="flex justify-between items-center gap-3">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Destino</span>
                <span className="text-[10px] font-black text-slate-700 uppercase text-right">
                  {destinoNombre}{destino.deposito ? ` · ${destino.deposito}` : ''}
                </span>
              </div>

              <div className="h-px bg-slate-200 opacity-60" />

              <div className="flex justify-between items-center">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Estado</span>
                <span className={`text-[10px] font-black uppercase ${estadoOperacion.tone}`}>{estadoOperacion.label}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Con error</span>
                <span className={`text-[10px] font-black uppercase ${conError > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                  {conError}
                </span>
              </div>
            </div>

            <div className="mt-4 flex gap-2 p-3 bg-blue-50/50 rounded-xl border border-blue-100">
              <Package size={14} className="text-[#51A7FD] shrink-0 mt-0.5" />
              <p className="text-[9px] font-bold text-blue-600 uppercase leading-tight tracking-wide">
                La transferencia valida el stock real del origen antes de confirmar.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-check-in { animation: checkIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes checkIn {
          from { transform: scale(0.92); opacity: 0.6; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default InventarioTransferencia;
